/**
 * Coups de pouce — Attendu 643 « Résoudre des problèmes impliquant des
 * longueurs ».
 *
 * Une entrée par `generator_key` (cf. gen_643.js). Contrat : pages (2 MAX),
 * chacune { titre, lignes?, svg? } — ici UNE page (lignes + schéma), format
 * validé en revue le 2026-07-30 : minimum de texte pour un 6e, le pivot
 * « 2 demi-cercles = 1 cercle entier » est MONTRÉ, pas raconté.
 *
 * Règle absolue : aucun nombre du domaine de tirage dans l'aide — le schéma
 * est volontairement SANS valeurs (formes pures), il ne peut rien divulguer.
 */

// ── Schéma : la piste se recompose en cercle ──
// Couleurs EXPLICITES (convention cp653) : jamais de currentColor — le
// thème sombre élève le rendrait blanc sur fond clair, donc invisible.
// Virages en orange #fb923c : le MÊME orange que la figure de l'exercice
// (svgPiste), pour que l'élève fasse le lien d'un coup d'œil.
const SVG_RECOMPOSITION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150" role="img"
     aria-label="Les deux virages en demi-cercle de la piste, recollés, forment un cercle entier">
  <line x1="60" y1="30" x2="160" y2="30" stroke="#0f172a" stroke-width="3"/>
  <line x1="60" y1="110" x2="160" y2="110" stroke="#0f172a" stroke-width="3"/>
  <path d="M 60 30 A 40 40 0 0 0 60 110" fill="none" stroke="#fb923c" stroke-width="4"/>
  <path d="M 160 30 A 40 40 0 0 1 160 110" fill="none" stroke="#fb923c" stroke-width="4"/>
  <path d="M 195 70 h 28 m -8 -7 l 8 7 l -8 7" fill="none" stroke="#0f172a" stroke-width="2.5"/>
  <circle cx="290" cy="70" r="40" fill="none" stroke="#fb923c" stroke-width="4"/>
  <text x="110" y="138" text-anchor="middle" font-size="12" fill="#64748b">2 demi-cercles</text>
  <text x="290" y="138" text-anchor="middle" font-size="12" fill="#64748b">1 cercle entier</text>
</svg>`.trim();

// ── Schéma jardin : 3 côtés clôturés + demi-cercle, le côté remplacé en
// pointillés (c'est le diamètre). Mêmes conventions que la figure de
// l'énoncé (svgJardin) : rectangle DEBOUT, demi-cercle posé dessus, orange
// pour la partie « cercle ». Aucune valeur — formes pures.
const SVG_JARDIN_CLOTURE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 190" width="132" height="114" role="img"
     aria-label="Jardin : trois côtés du rectangle et le demi-cercle sont clôturés, le côté du haut, en pointillés, est le diamètre du demi-cercle">
  <line x1="70" y1="70" x2="70" y2="170" stroke="#0f172a" stroke-width="3"/>
  <line x1="70" y1="170" x2="150" y2="170" stroke="#0f172a" stroke-width="3"/>
  <line x1="150" y1="70" x2="150" y2="170" stroke="#0f172a" stroke-width="3"/>
  <line x1="70" y1="70" x2="150" y2="70" stroke="#64748b" stroke-width="2" stroke-dasharray="5,4"/>
  <path d="M 70 70 A 40 40 0 0 1 150 70" fill="none" stroke="#fb923c" stroke-width="4"/>
  <text x="110" y="90" text-anchor="middle" font-size="15" font-style="italic" fill="#64748b">D</text>
</svg>`.trim();

const CP_643 = {
    probleme_longueurs_qcm_unique: {
        pages: [
            {
                titre: 'Le tour complet',
                lignes: [
                    '**Découpe** la figure : deux lignes droites + deux demi-cercles.',
                    'Les deux demi-cercles forment **un cercle entier** : son tour = **2 × π × rayon**.',
                    'Tour complet = les deux droites **+** le cercle. Compare aux **deux bornes** de l\'énoncé.',
                ],
                svg: SVG_RECOMPOSITION,
            },
            {
                // Valeurs HORS domaine de tirage (L ∈ [70;90], R ∈ [30;40,5])
                // et bornes fictives : la méthode complète, jamais la réponse.
                titre: 'Un exemple',
                lignes: [
                    'Piste : deux droites de **50 m**, deux virages de rayon **20 m**.',
                    'Les deux droites : 2 × 50 = **100 m**.',
                    'Le cercle entier : 2 × π × 20 ≈ **125,66 m**.',
                    'Tour complet : 100 + 125,66 ≈ **225,66 m**. Entre 220 et 230 ? **Oui.**',
                ],
            },
        ],
    },

    // Format raisonnement à trous (toujours la piste) : UNE page seulement —
    // les trous guident déjà pas à pas, un exemple chiffré ferait le travail à
    // la place de l'élève (décision revue 2026-07-31). La page rappelle le
    // pivot (2 demi-cercles = 1 cercle) et le geste final (comparer aux bornes).
    probleme_longueurs_texte_trou_saisie: {
        pages: [
            {
                titre: 'Le tour complet',
                lignes: [
                    '**Découpe** la figure : deux lignes droites + deux demi-cercles.',
                    'Les deux demi-cercles forment **un cercle entier** : son tour = **2 × π × rayon**.',
                    'Tour complet = les deux droites **+** le tour du cercle. Homologuée si le total est **entre 400 m et 402,3 m**.',
                ],
                svg: SVG_RECOMPOSITION,
            },
        ],
    },

    // Format saisie, 4 contextes → forme à VARIANTES (cf. utils/coupDePouce.js).
    // Chaque motif est un mot EXCLUSIF à son contexte (vérifié par
    // tmp-643-texte.test.js sur 5000 tirages : exactement 1 motif par énoncé).
    // `defaut` = filet si un énoncé est reformulé un jour sans son motif.
    probleme_longueurs_texte: {
        variantes: [
            {
                sousType: 'piste',
                pages: [
                    {
                        titre: 'Le tour complet',
                        lignes: [
                            '**Découpe** la figure : deux lignes droites + deux demi-cercles.',
                            'Les deux demi-cercles forment **un cercle entier** : son tour = **2 × π × rayon**.',
                            'Tour complet = les deux droites **+** le tour du cercle. Arrondis comme demandé.',
                        ],
                        svg: SVG_RECOMPOSITION,
                    },
                ],
            },
            {
                sousType: 'piscine',
                pages: [
                    {
                        titre: 'Le tour complet',
                        lignes: [
                            '**Découpe** la figure : deux longueurs droites + deux demi-cercles.',
                            'Les deux demi-cercles forment **un cercle entier**. Son diamètre, c\'est **la largeur** de la piscine : tour du cercle = **π × diamètre**.',
                            'Tour complet = les deux longueurs **+** le tour du cercle.',
                        ],
                        svg: SVG_RECOMPOSITION,
                    },
                ],
            },
            {
                sousType: 'jardin',
                pages: [
                    {
                        titre: 'La clôture',
                        lignes: [
                            'Le grillage suit **3 côtés** du rectangle, puis le **demi-cercle**.',
                            'Le côté en pointillés n\'est **pas clôturé** : c\'est le **diamètre** du demi-cercle.',
                        ],
                        svg: SVG_JARDIN_CLOTURE,
                    },
                    {
                        // Valeurs HORS domaine (L ∈ [8;18], l ∈ [5;12]) : la
                        // méthode complète — cercle entier puis ÷ 2 — jamais la réponse.
                        titre: 'Un exemple',
                        lignes: [
                            'Jardin : rectangle de **30 m** de long et **20 m** de large.',
                            'Les 3 côtés : 30 + 30 + 20 = **80 m**.',
                            'Le cercle entier : π × 20 ≈ **62,83 m** → le demi-cercle : 62,83 ÷ 2 ≈ **31,42 m**.',
                            'Grillage : 80 + 31,42 ≈ **111,42 m**.',
                        ],
                    },
                ],
            },
            {
                sousType: 'ruban',
                pages: [
                    {
                        titre: 'Le tour du cercle',
                        lignes: [
                            'Le tour d\'un cercle = **π × diamètre**.',
                        ],
                    },
                ],
            },
        ],
        defaut: {
            pages: [
                {
                    titre: 'Découpe la figure',
                    lignes: [
                        'Découpe la figure en morceaux simples : des côtés **droits** et des morceaux de **cercle**.',
                        'Calcule chaque morceau, puis **additionne** tout.',
                    ],
                },
            ],
        },
    },
};

export default CP_643;
